/**
 * Height-profile (rainbow) scale of a detected surface — the shader-facing math.
 *
 * Membership stays on the grown plane (`plane.origin` = the pick, grown
 * `maxDistance` as slab). Colour is anchored elsewhere: the zero level is the
 * member centroid returned by grow, shifted by the user's level offset, and
 * the gradient reaches the user's range below (hollow side) and range above
 * (proud side) — two independent half-widths, so the fill and the chip-off
 * can be inspected at different scales. All are shader-only per-surface
 * uniforms, so dragging never re-grows. Kept free of `Surface.js`
 * so `PointCloudMaterial.js` can import it without pulling the tool stack in;
 * the slider bounds / view-state clamps live next to `clampExtraPlasterM`.
 */
import { residualToPlane } from './surfaceMembership.js';

function finiteVec3(v) {
  return Array.isArray(v) && v.length >= 3 && Number.isFinite(v[0]) && Number.isFinite(v[1]) && Number.isFinite(v[2]);
}

/** Signed distance of the centroid from the grown plane (pre-flip normal); 0 when unknown. */
export function profileLevelFromCentroid(centroid, plane) {
  if (!finiteVec3(centroid) || !plane || !finiteVec3(plane.origin) || !finiteVec3(plane.normal)) return 0;
  return residualToPlane(centroid, plane.origin, plane.normal);
}

/**
 * Zero level in the flip-aware frame the shader colours in:
 * `dot(P − C − offset·sign·n, sign·n) = sign·residual − (sign·level0 + offset)`.
 * A positive offset always moves the zero toward "proud".
 */
export function profileLevelM(flipped, level0, offsetM) {
  const sign = flipped ? -1 : 1;
  const l0 = Number.isFinite(level0) ? level0 : 0;
  const off = Number.isFinite(offsetM) ? offsetM : 0;
  return sign * l0 + off;
}

/** Pinned range (either side, 0 included), else the grown slab half-width, else `fallback`. */
export function effectiveProfileRangeM(profileRangeM, grownMaxDistance, fallback = 0.05) {
  if (Number.isFinite(profileRangeM) && profileRangeM >= 0) return profileRangeM;
  if (Number.isFinite(grownMaxDistance) && grownMaxDistance > 0) return grownMaxDistance;
  return fallback;
}

/** 45° snap threshold between "wall" (plumb) and "floor / ceiling" (level). */
export const IDEAL_HORIZONTAL_COS = Math.cos(Math.PI / 4);

/**
 * Snap a fitted normal to the cloud's own axes (Z is the scanner's vertical):
 * floor / ceiling (`|n.z| > cos 45°`) → `[0, 0, ±1]` (perfectly level), wall →
 * `[n.x, n.y, 0]` renormalised (perfectly plumb, same azimuth). The input is
 * normalised first — restored planes are copied verbatim by `ViewState.js` and
 * the threshold is only an angle test on a unit vector. Exactly `cos 45°` is a
 * wall. The sign follows `n`, so `dot(n, nIdeal) ≥ cos 45° > 0` and `flipped`
 * keeps meaning "swap hollow / proud".
 */
export function idealProfileNormal(normal) {
  if (!finiteVec3(normal)) return [0, 0, 1];
  const len = Math.hypot(normal[0], normal[1], normal[2]);
  if (!(len > 1e-9)) return [0, 0, 1];
  const z = normal[2] / len;
  if (Math.abs(z) > IDEAL_HORIZONTAL_COS) return [0, 0, z > 0 ? 1 : -1];
  const xy = Math.hypot(normal[0], normal[1]);
  if (!(xy > 1e-9)) return [0, 0, 1];
  return [normal[0] / xy, normal[1] / xy, 0];
}

/** The normal the height-profile colours against: snapped in ideal mode, the fitted plane otherwise. */
export function profileNormal(profileRef, planeNormal) {
  // Literal instead of PROFILE_REF.IDEAL: this module stays free of `Surface.js`.
  if (profileRef === 'ideal') return idealProfileNormal(planeNormal);
  if (!finiteVec3(planeNormal)) return [0, 0, 1];
  return [planeNormal[0], planeNormal[1], planeNormal[2]];
}

/** Angle (degrees) between the fitted plane and the colouring plane; 0 in fitted mode or on invalid input. */
export function profileTiltDeg(planeNormal, profileNormalVec) {
  if (!finiteVec3(planeNormal) || !finiteVec3(profileNormalVec)) return 0;
  const la = Math.hypot(planeNormal[0], planeNormal[1], planeNormal[2]);
  const lb = Math.hypot(profileNormalVec[0], profileNormalVec[1], profileNormalVec[2]);
  if (!(la > 1e-9) || !(lb > 1e-9)) return 0;
  const dot =
    (planeNormal[0] * profileNormalVec[0] +
      planeNormal[1] * profileNormalVec[1] +
      planeNormal[2] * profileNormalVec[2]) /
    (la * lb);
  return (Math.acos(Math.min(1, Math.max(-1, dot))) * 180) / Math.PI;
}

/**
 * CPU mirror of the rainbow branch in `pointcloud.vs`: gradient coordinate in
 * [0, 1], or `null` beyond −rangeBelow … +rangeAbove where the point keeps
 * its own colour. Each side of the zero level has its own scale — the hollow
 * half of the ramp spans −rangeBelow … 0, the proud half 0 … +rangeAbove
 * (defaults to the same value, i.e. the old symmetric range); a zero side
 * paints nothing on that side.
 */
export function profileWeight(residual, flipped, levelM, rangeBelowM, rangeAboveM = rangeBelowM) {
  const signedR = (flipped ? -residual : residual) - levelM;
  if (signedR < -rangeBelowM || signedR > rangeAboveM) return null;
  const range = signedR < 0 ? rangeBelowM : rangeAboveM;
  const w = 0.5 + (0.5 * signedR) / Math.max(range, 1e-4);
  return Math.min(1, Math.max(0, w));
}

/**
 * CPU mirror of the banding step in `pointcloud.vs`: which of `bands` discrete
 * colours the gradient coordinate `w` falls in. `w < 0.5 ⇔ k < bands / 2`, so
 * an even band count splits exactly half below / half above the zero level
 * (the zero level itself lands on the proud side). `bands < 2` → -1 (continuous).
 */
export function profileBandIndex(w, bands) {
  if (!(bands >= 2)) return -1;
  return Math.min(Math.floor(w * bands), bands - 1);
}

/** Gradient coordinate of the band centre `(k + 0.5) / N`; unchanged when continuous. */
export function profileBandWeight(w, bands) {
  if (!(bands >= 2)) return w;
  return (profileBandIndex(w, bands) + 0.5) / bands;
}

/**
 * Band boundaries in metres, ascending, k = 0..N: the N/2 hollow bands split
 * −rangeBelow … 0 evenly, the N/2 proud bands 0 … +rangeAbove, so
 * `b_k = −below · (1 − 2k / N)` for k ≤ N/2 and `above · (2k / N − 1)` above it.
 * Band k covers `[b_k, b_{k+1})`; zero is always an edge. `[]` when continuous.
 */
export function profileBandEdgesM(rangeBelowM, rangeAboveM, bands) {
  if (!(bands >= 2)) return [];
  const below = Number.isFinite(rangeBelowM) ? rangeBelowM : 0;
  const above = Number.isFinite(rangeAboveM) ? rangeAboveM : 0;
  const edges = [];
  for (let k = 0; k <= bands; k++) {
    const t = (2 * k) / bands - 1;
    edges.push(t < 0 ? below * t : above * t);
  }
  return edges;
}

/**
 * Decimals for the legend edges so adjacent rows never print the same text:
 * the finer band step of the two sides is `2·min(below, above) / N` cm; 1
 * decimal down to a 0.1 cm step, then 2, then 3 (0.1 cm is the smallest
 * non-zero slider value, i.e. a 0.025 cm step at N=8). A zero side has no
 * rows (see `profileBandLegendRows`), so it does not count.
 */
export function bandEdgeDecimals(rangeBelowCm, rangeAboveCm, bands) {
  const n = bands >= 2 ? bands : 2;
  const sides = [rangeBelowCm, rangeAboveCm].filter((v) => Number.isFinite(v) && v !== 0).map(Math.abs);
  const step = sides.length ? (2 * Math.min(...sides)) / n : 1;
  if (step < 0.01) return 3;
  if (step < 0.1) return 2;
  return 1;
}

/**
 * Legend rows, proud at the top: `{ band, text }` per band that can actually
 * be painted — a zero side has no width, so its N/2 bands are left out rather
 * than listed as `0.0 … 0.0`. `[]` when continuous.
 */
export function profileBandLegendRows(rangeBelowM, rangeAboveM, bands) {
  if (!(bands >= 2)) return [];
  const belowCm = (Number.isFinite(rangeBelowM) ? rangeBelowM : 0) * 100;
  const aboveCm = (Number.isFinite(rangeAboveM) ? rangeAboveM : 0) * 100;
  const edgesCm = profileBandEdgesM(rangeBelowM, rangeAboveM, bands).map((m) => m * 100);
  const decimals = bandEdgeDecimals(belowCm, aboveCm, bands);
  const rows = [];
  for (let k = bands - 1; k >= 0; k--) {
    // The outer edges are the ranges themselves (points beyond keep their colour).
    const lo = edgesCm[k];
    const hi = edgesCm[k + 1];
    if (!(hi > lo)) continue;
    rows.push({ band: k, text: `${formatBandEdgeCm(lo, decimals)} … ${formatBandEdgeCm(hi, decimals)} cm` });
  }
  return rows;
}

/** Signed cm at `decimals`: `+2.5`, `−2.5` (U+2212), plain `0.0` — never `−0.0`. */
export function formatBandEdgeCm(valueCm, decimals = 1) {
  const v = Number.isFinite(valueCm) ? valueCm : 0;
  const abs = Math.abs(v).toFixed(decimals);
  if (Number(abs) === 0) return abs;
  return `${v > 0 ? '+' : '−'}${abs}`;
}

/**
 * Linear interpolation of a Potree gradient (`[[stop, {r, g, b}], …]`, stops
 * ascending in [0, 1]) at `t`, clamped to [0, 1]. Colours are read as plain
 * `r, g, b` objects so this module stays free of `three`. Returns `{ r, g, b }` in 0..1.
 */
export function profileBandColor(gradient, t) {
  const stops = Array.isArray(gradient) ? gradient : [];
  if (stops.length === 0) return { r: 0, g: 0, b: 0 };
  const x = Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0));
  const pick = (c) => ({ r: c.r, g: c.g, b: c.b });
  if (x <= stops[0][0]) return pick(stops[0][1]);
  for (let i = 1; i < stops.length; i++) {
    const [s1, c1] = stops[i];
    if (x <= s1) {
      const [s0, c0] = stops[i - 1];
      const span = s1 - s0;
      const f = span > 0 ? (x - s0) / span : 1;
      return {
        r: c0.r + (c1.r - c0.r) * f,
        g: c0.g + (c1.g - c0.g) * f,
        b: c0.b + (c1.b - c0.b) * f,
      };
    }
  }
  return pick(stops[stops.length - 1][1]);
}
